import { Component, OnInit, Input, Output, EventEmitter, HostListener, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { Router, ActivatedRoute, NavigationExtras } from '@angular/router';
import { LoadingService } from 'src/app/services/loading.service';
import { Subscription } from 'rxjs';
import User from 'src/app/interfaces/User';
import { UserService } from 'src/app/services/user.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import * as moment from 'moment-timezone';
import { ForumService } from 'src/app/services/forum.service';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  host: {
    '(document:click)': 'onClick($event)',
  },
})
export class FilterComponent implements OnInit, OnDestroy {
  @ViewChild('sortEl', { static: false }) sortEl: ElementRef;
  @Input() list;
  @Input() listLength;
  @Input() settings;
  @Input() primaryButton;
  @Input() secondaryButton;
  @Output() filterChange: EventEmitter<any> = new EventEmitter();
  subscriptions: Subscription = new Subscription();
  currentUser: User;
  /* filters */
  filteredList = [];
  search = {
    term: null,
    isFocused: false,
  };
  approach = {
    toggled: false,
    selected: [],
  };
  keyword = {
    toggled: false,
    selected: [],
  };
  tags = {
    standard: [
      'technology',
      'environmental-impacts',
      'governance',
      'markets-finance',
      'funding',
      'cdr-community',
      'social-licensing',
    ],
    approaches: ['microalgae', 'blue-carbon', 'macroalgal', 'alkalinity', 'co2-removal', 'storage', 'other'],
  };
  newParam = false;
  showClearIcon = false;
  sortDropdownToggled = false;
  sortSelection = 'Newest';
  sortOptions = ['Newest', 'Oldest', 'Most comments', 'Most liked'];

  scrolled = false;
  viewAmount;

  constructor(
    private viewportScroller: ViewportScroller,
    private router: Router,
    private route: ActivatedRoute,
    private loadingService: LoadingService,
    private analyticsService: AnalyticsService,
    private userService: UserService,
    private forumService: ForumService
  ) {}

  ngOnInit() {
    this.subscriptions.add(
      this.userService.user.subscribe((u) => {
        if (u && u.uid) {
          this.currentUser = u;
        }
      })
    );

    let int = setInterval(() => {
      if (this.list && this.list.length && this.list.length > 1) {
        this.setViewAmount();
        if (this.settings.type === 'forum') {
          this.sortOptions.unshift('Recent Activity')
          this.sortSelection = 'Recent Activity';
        }
        if (this.settings.type === 'members') {
          this.sortSelection = 'Alphabetical';
          this.sortOptions = ['Alphabetical', 'Most comments'];
        }
        if (
          this.settings.type === 'projects' ||
          this.settings.type === 'institutions' ||
          this.settings.type === 'jobs'
        ) {
          this.sortOptions = ['Alphabetical', 'Newest', 'Oldest'];
        }
        if (this.settings.type === 'events') {
          this.sortOptions = ['Alphabetical', 'Chronological', 'Newest', 'Oldest'];
          this.sortSelection = 'Chronological';
        }
        this.selectSortOption(this.sortSelection);
        this.toggleSortDropdown();
        
        const approachFilter: string = this.route.snapshot.queryParamMap.get('approach');
        if (approachFilter && this.settings.type === 'resources') this.loadingService.startLoading();
        if (
          (approachFilter && this.settings.type !== 'resources') || 
          (approachFilter && this.settings.type === 'resources' && this.list.length > 50)
        ) {
          this.toggleDropdown(this.approach, this.keyword);
          this.toggleApproach(approachFilter);
          this.loadingService.stopLoading();
          clearInterval(int);
        } else {
          clearInterval(int);
        }

        // init url params
        this.route.queryParams.subscribe(params => {
          let search = params['s'];
          let tagInd = params['t'];
          
          if (search) this.search.term = search;
          if (tagInd) {
            let tagsArr = tagInd.split(',').map(ind => {
              return this.getAllTags()[parseInt(ind)]
            });
            tagsArr.forEach(tag => {
              let keyword = this.tags.standard.includes(tag);
              let approach = this.tags.approaches.includes(tag)

              if (keyword) {
                this.keyword.selected.push(tag);
              }
              if (approach) {
                this.approach.selected.push(tag);
              }
            })
          }

          this.triggerSearch();
        }).unsubscribe();
      }
    }, 100);

  }

  onChange() {
    this.filterChange.emit({
      search: this.search.term,
      approaches: this.approach.selected,
      keywords: this.keyword.selected,
      filteredList: this.filteredList,
    });
    this.setViewAmount();
  }

  setViewAmount() {
    if (
      (this.search.term && this.search.term.length) ||
      this.approach.selected.length > 0 ||
      this.keyword.selected.length > 0
    ) {
      if (this.filteredList && this.filteredList.length) {
        this.viewAmount = this.filteredList.length;
      } else {
        this.viewAmount = 0;
      }
    } else {
      this.viewAmount = this.list.length;
    }
  }

  toggleSortDropdown() {
    this.sortDropdownToggled = !this.sortDropdownToggled;
  }
  selectSortOption(o) {
    this.sortSelection = o;
    this.toggleSortDropdown();
    if (this.sortSelection === 'Newest') {
      this.filteredList = this.list.sort((a, b) => {
        if (!!a.timestamp && !!b.timestamp) {
          return b.timestamp.seconds - a.timestamp.seconds;
        }
      });
    } else if (this.sortSelection === 'Oldest') {
      this.filteredList = this.list.sort((a, b) => {
        if (!!a.timestamp && !!b.timestamp) {
          return a.timestamp.seconds - b.timestamp.seconds;
        }
      });
    } else if (this.sortSelection === 'Most comments') {
      this.filteredList = this.list.sort((a, b) => {
        if (!a.comments) {
          a.comments = [];
        }
        if (!b.comments) {
          b.comments = [];
        }
        return b.comments.length - a.comments.length;
      });
    } else if (this.sortSelection === 'Most liked') {
      this.filteredList = this.list.sort((a, b) => {
        return b.reactions.length - a.reactions.length;
      });
    } else if (this.sortSelection === 'Alphabetical') {
      if (this.settings.type === 'members') {
        this.filteredList = this.list.sort((a, b) => {
          if (a.lastName && b.lastName) {
            if (a.lastName.toLowerCase() < b.lastName.toLowerCase()) return -1;
            if (a.lastName.toLowerCase() > b.lastName.toLowerCase()) return 1;
            return 0;
          }
        });
      } else if (
        this.settings.type === 'projects' ||
        this.settings.type === 'institutions' ||
        this.settings.type === 'events' ||
        this.settings.type === 'jobs'
      ) {
        ['projectName', 'institutionName', 'title'].forEach((label, i) => {
          this.filteredList = this.list.sort((a, b) => {
            if (a[label] && b[label]) {
              if (a[label].toLowerCase() < b[label].toLowerCase()) return -1;
              if (a[label].toLowerCase() > b[label].toLowerCase()) return 1;
              return 0;
            }
          });
        });
      }
    } else if (this.sortSelection === 'Chronological') {
      this.filteredList = this.list.sort((a,b) => {
        let d1 = new Date(
          a.dateTime.startDate + 
          (a.dateTime.startTime ? (' ' + a.dateTime.startTime) : '')
        ).getTime()
        let d2 = new Date(
          b.dateTime.startDate + 
          (b.dateTime.startTime ? (' ' + b.dateTime.startTime) : '')
        ).getTime();
        return d1 - d2;
      })
    } else if (this.sortSelection === 'Recent Activity') {
      this.filteredList = this.list.sort((a,b) => {
        let aTimestamps = [a.timestamp.seconds]
        .concat(a.comments && a.comments.length ? a.comments.map((c) => c.timestamp.seconds ) : [])
        .sort((a,b) => b - a);
        let bTimestamps = [b.timestamp.seconds]
        .concat(b.comments && b.comments.length ? b.comments.map((c) => c.timestamp.seconds ) : [])
        .sort((a,b) => b - a);
        return bTimestamps[0] - aTimestamps[0];
      })
    }
    this.analyticsService.event(`${this.settings.type}_apply_sorting`, { sort: this.sortSelection });
    this.onChange();
  }

  toggleDropdown(target, toHide) {
    target.toggled = !target.toggled;
    toHide.toggled = false;
  }

  toggleApproach(a): void {
    if (!this.approach.selected || this.approach.selected.indexOf(a) === -1) {
      this.approach.selected.push(a);
      this.analyticsService.event(`${this.settings.type}_apply_approach`, { approach: a });
    } else {
      this.approach.selected.splice(this.approach.selected.indexOf(a), 1);
    }
    this.approach.toggled = false;
    this.triggerSearch();
  }

  toggleTag(t): void {
    if (!this.keyword.selected || this.keyword.selected.indexOf(t) === -1) {
      this.keyword.selected.push(t);
      this.analyticsService.event(`${this.settings.type}_apply_keyword`, { keyword: t });
    } else {
      this.keyword.selected.splice(this.keyword.selected.indexOf(t), 1);
    }
    this.keyword.toggled = false;
    this.triggerSearch();
  }

  triggerSearch(): void {
    this.filteredList = [];
    let searchList = [];
    let tagList = [];
    const searchActive = this.search.term && this.search.term.length > 0;
    const tagsActive = this.approach.selected.length > 0 || this.keyword.selected.length > 0;

    // if search
    if (searchActive) {
      const term = this.search.term.toLowerCase();
      this.analyticsService.event(`${this.settings.type}_search`, { search_term: term });

      searchList = this.list.filter((item) => {
        let compareStr = '';
        if (this.settings && this.settings.type === 'forum') {
          if (item.prompt) compareStr += item.prompt.toLowerCase();
          if (item.content) compareStr += item.content.toLowerCase();
          if (item.ownerMeta && item.ownerMeta.name) compareStr += item.ownerMeta.name.toLowerCase();
        } else if (this.settings && this.settings.type === 'institutions') {
          if (item.institutionName) compareStr += item.institutionName.toLowerCase();
          if (item.ownerMeta && item.ownerMeta.name) compareStr += item.ownerMeta.name.toLowerCase();
          if (item.summary) compareStr += item.summary.toLowerCase();
        } else if (this.settings && this.settings.type === 'projects') {
          if (item.projectName) compareStr += item.projectName.toLowerCase();
          if (item.ownerMeta && item.ownerMeta.name) compareStr += item.ownerMeta.name.toLowerCase();
          if (item.summary) compareStr += item.summary.toLowerCase();
        } else if (this.settings && this.settings.type === 'events') {
          if (item.title) compareStr += item.title.toLowerCase();
          if (item.ownerMeta && item.ownerMeta.name) compareStr += item.ownerMeta.name.toLowerCase();
          if (item.description) compareStr += item.description.toLowerCase();
        } else if (this.settings && this.settings.type === 'members') {
          if (item.displayName) compareStr += item.displayName.toLowerCase();
          if (item.institution) compareStr += item.institution.toLowerCase();
          if (item.position) compareStr += item.position.toLowerCase();
        } else if (this.settings && this.settings.type === 'jobs') {
          if (item.title) compareStr += item.title.toLowerCase();
          if (item.summary) compareStr += item.summary.toLowerCase();
        } else if (this.settings && this.settings.type === 'resources') {
          if (item.description) compareStr += item.description.toLowerCase();
          if (item.leadAuthor) compareStr += item.leadAuthor.toLowerCase();
          if (item.title) compareStr += item.title.toLowerCase();
          if (item.ownerMeta && item.ownerMeta.name) compareStr += item.ownerMeta.name.toLowerCase();
          if (item.coAuthors) compareStr += item.coAuthors.join(' ').toLowerCase();
        }
        return compareStr.includes(term);
      });

      this.showClearIcon = true;
    }

    // if tags
    if (tagsActive) {
      // Figure out if each list item contains each selected tag
      // There are three ways that tags are stored on list items
      //    - approaches: []  (only approaches)
      //    - tags: []        (only tags)
      //    - expertise: []   (combined approaches and tags)
      //
      // First combine selected approaches and keywords
      const selectedCombined = [...this.approach.selected, ...this.keyword.selected];

      // Then combine list item approaches and tags if they exist
      tagList = this.list.filter((listItem) => {
        let listItemCombined = [];
        if ((listItem.approaches && listItem.approaches.length) || (listItem.tags && listItem.tags.length)) {
          listItemCombined = [...listItem.approaches, ...listItem.tags];
        } else if (listItem.expertise) {
          listItemCombined = [...listItem.expertise];
        }

        // Then compare selectedCombined and listItemCombined
        return selectedCombined.every((el) => listItemCombined.includes(el));
      });
    }

    // search and tags
    if (searchActive && tagsActive) {
      this.filteredList = this.list.filter((listItem) => {
        return searchList.some((li) => li.id === listItem.id) && tagList.some((li) => li.id === listItem.id);
      });

      // just search
    } else if (searchActive) {
      this.filteredList = searchList;

      // just tags
    } else if (tagsActive) {
      this.filteredList = tagList;

      // empty
    } else {
      this.filteredList = this.list;
    }

    this.updateUrl();

    this.onChange();
  }

  updateUrl() {
    
    let search = this.search.term;
    let tags = this.getAllSelectedTags().map(tag => {   // map selected tags
      return this.getAllTags().indexOf(tag);            // get index of selected tag in full tag list 
    }).sort().join();

    let navOpts: NavigationExtras = {
      relativeTo: this.route,
      queryParams: {},
      queryParamsHandling: 'merge',
      skipLocationChange: false
    }

    navOpts.queryParams['s'] = search ? search : null;
    navOpts.queryParams['t'] = tags ? tags : null;

    this.router.navigate([], navOpts);
  }

  getAllSelectedTags() {
    return [...this.approach.selected, ...this.keyword.selected]
  }
  getAllTags() {
    return [...this.tags.standard, ...this.tags.approaches];
  }

  matchTags(item) {
    let match = false;
    this.approach.selected.forEach((a) => {
      ['approaches', 'expertise'].forEach((str) => {
        if (item[str] && item[str].length > 0 && item[str].indexOf(a) !== -1) {
          match = true;
        }
      });
    });
    this.keyword.selected.forEach((k) => {
      ['tags', 'expertise'].forEach((str) => {
        if (item[str] && item[str].length > 0 && item[str].indexOf(k) !== -1) {
          match = true;
        }
      });
    });
    return match;
  }

  trackEvent(name: string) {
    if (name === 'new') {
      this.analyticsService.event(`${this.settings.type}_add_click`);
    }
  }

  @HostListener('window:scroll', ['$event']) // for window scroll events
  onScroll(event) {
    if (this.viewportScroller.getScrollPosition()[1] > 50) {
      this.scrolled = true;
    } else {
      this.scrolled = false;
    }
  }
  scrollToTop() {
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  clearSearch() {
    this.search = {
      term: null,
      isFocused: false,
    };
    this.showClearIcon = false;
    this.updateUrl();
    this.triggerSearch();
  }

  onClick(event) {
    if (!this.sortEl.nativeElement.contains(event.target)) {
      if (this.sortDropdownToggled) {
        this.sortDropdownToggled = false;
      }
    }
  }
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
